// DEPLOY: /var/www/woody.wtf/frontend/src/components/AlertBell.js
// Navbar bell: unread count of the user's alerts (/api/alerts/unread, checked every minute and
// when the tab regains focus) and a dropdown inbox (/api/alerts). Choosing an alert marks it
// read and opens its chart on /chart. Phones: the inbox drops down full width.
import React, { useEffect, useRef, useState } from 'react';
import api from '../api';

const POLL_MS = 60000;

const S = {
  wrap:   { position: 'relative', display: 'flex', alignItems: 'center' },
  btn:    { position: 'relative', border: 'none', background: 'none', cursor: 'pointer', padding: '6px', color: 'var(--text-muted)', display: 'flex', alignItems: 'center' },
  count:  { position: 'absolute', top: '0px', right: '-2px', minWidth: '16px', height: '16px', padding: '0 4px', borderRadius: '8px', background: 'var(--error)', color: 'var(--on-accent)', fontSize: '10px', fontWeight: 700, lineHeight: '16px', textAlign: 'center', boxSizing: 'border-box', fontFamily: "'JetBrains Mono',monospace" },
  drop:   { position: 'absolute', top: '44px', right: 0, width: '360px', maxHeight: '70vh', overflowY: 'auto', background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 'var(--radius)', boxShadow: 'var(--shadow)', zIndex: 2000 },
  dropM:  { position: 'fixed', top: '56px', left: '8px', right: '8px', maxHeight: '75vh', overflowY: 'auto', background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 'var(--radius)', boxShadow: 'var(--shadow)', zIndex: 2000 },
  head:   { display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px 12px', borderBottom: '1px solid var(--border)', position: 'sticky', top: 0, background: 'var(--surface)' },
  title:  { fontSize: '13px', fontWeight: 700, color: 'var(--text)' },
  all:    { border: 'none', background: 'none', color: 'var(--accent)', fontSize: '12px', cursor: 'pointer' },
  item:   { display: 'block', width: '100%', textAlign: 'left', padding: '10px 12px', border: 'none', borderBottom: '1px solid var(--border)', background: 'none', cursor: 'pointer', color: 'var(--text)' },
  itTop:  { display: 'flex', justifyContent: 'space-between', gap: '8px', fontSize: '13px', fontWeight: 600 },
  itDate: { fontSize: '11px', color: 'var(--text-muted)', fontFamily: "'JetBrains Mono',monospace", whiteSpace: 'nowrap', fontWeight: 400 },
  itBody: { fontSize: '12px', color: 'var(--text-muted)', marginTop: '3px', whiteSpace: 'pre-line' },
  empty:  { padding: '20px', textAlign: 'center', color: 'var(--text-muted)', fontSize: '13px' },
};

const Bell = () => (
  <svg width="19" height="19" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <path d="M6 8a6 6 0 0 1 12 0c0 7 3 9 3 9H3s3-2 3-9" /><path d="M10.3 21a1.94 1.94 0 0 0 3.4 0" />
  </svg>
);

export default function AlertBell() {
  const [unread, setUnread] = useState(0);
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState(null);
  const [mobile, setMobile] = useState(() => window.matchMedia('(max-width: 768px)').matches);
  const ref = useRef(null);

  useEffect(() => {
    const check = () => api.get('/alerts/unread').then(r => setUnread(r.data.unread || 0)).catch(() => {});
    check();
    const t = setInterval(check, POLL_MS);
    window.addEventListener('focus', check);
    const mq = window.matchMedia('(max-width: 768px)'), onMq = () => setMobile(mq.matches);
    mq.addEventListener('change', onMq);
    return () => { clearInterval(t); window.removeEventListener('focus', check); mq.removeEventListener('change', onMq); };
  }, []);

  useEffect(() => {
    if (!open) return;
    api.get('/alerts?limit=50').then(r => { setItems(r.data.items || []); setUnread(r.data.unread || 0); }).catch(() => setItems([]));
    const close = e => { if (!ref.current?.contains(e.target)) setOpen(false); };
    document.addEventListener('pointerdown', close);
    return () => document.removeEventListener('pointerdown', close);
  }, [open]);

  const markRead = body => api.post('/alerts/read', body).catch(() => {});

  const choose = async it => {
    if (!it.read) await markRead({ ids: [it.id] });
    if (it.ticker) window.location.assign(`/chart?t=${encodeURIComponent(it.ticker)}`);
    else { setItems(list => list.map(x => (x.id === it.id ? { ...x, read: true } : x))); setUnread(u => Math.max(0, u - 1)); }
  };

  const readAll = async () => {
    await markRead({ all: true });
    setItems(list => (list || []).map(x => ({ ...x, read: true }))); setUnread(0);
  };

  return (
    <div ref={ref} style={S.wrap}>
      <button style={{ ...S.btn, color: open ? 'var(--accent)' : S.btn.color }} onClick={() => setOpen(o => !o)}
              title={unread ? `${unread} new alert${unread === 1 ? '' : 's'}` : 'Alerts'}>
        <Bell />
        {unread > 0 && <span style={S.count}>{unread > 99 ? '99+' : unread}</span>}
      </button>
      {open && (
        <div style={mobile ? S.dropM : S.drop}>
          <div style={S.head}>
            <span style={S.title}>Alerts</span>
            {unread > 0 && <button style={S.all} onClick={readAll}>Mark all read</button>}
          </div>
          {items === null && <div style={S.empty}>Loading…</div>}
          {items?.length === 0 && <div style={S.empty}>No alerts yet. Draw an alert line on a chart (Alerts group).</div>}
          {items?.map(it => (
            <button key={it.id} style={{ ...S.item, background: it.read ? 'none' : 'var(--accent-faint)' }} onClick={() => choose(it)}>
              <div style={S.itTop}><span>{it.title}</span><span style={S.itDate}>{it.d || (it.created_at || '').slice(0, 10)}</span></div>
              {it.body && <div style={S.itBody}>{it.body}</div>}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
